import { useDispatch, useSelector } from "react-redux";
import { useEffect, useMemo } from "react";
import { debounce } from "lodash";
import { ActionType, AppDispatch } from "../reducer";
import { AddServiceAccountsAction } from "../reducer/serviceAccount";
import { ServiceAccount } from "../types/serviceAccount";
import {
  makeSelectServiceAccount,
  selectServiceAccounts,
} from "../selectors/ui";
import { getServiceAccount } from "../api/serviceAccount";
import { writeData } from "../api";
import { loadKey, loadStatus } from "./loadStatus";
import { requireRecord, useTrackedLoad } from "./loadTracking";
import { useLoadError } from "../slices/ui";

export const useAddServiceAccounts = () => {
  const dispatch = useDispatch<AppDispatch>();
  return (serviceAccounts: ServiceAccount[]) => {
    const action: AddServiceAccountsAction = {
      type: ActionType.ADD_SERVICE_ACCOUNTS,
      serviceAccounts,
    };
    dispatch(action as any);
  };
};

export const useServiceAccount = (serviceAccountId: string | undefined) => {
  const selectServiceAccount = useMemo(
    () => makeSelectServiceAccount(serviceAccountId),
    [serviceAccountId],
  );
  const serviceAccount = useSelector(selectServiceAccount);
  const writeBackend = useMemo(
    () =>
      debounce((sa: ServiceAccount) => {
        writeData(sa);
      }, 500),
    [serviceAccountId],
  );

  const addServiceAccounts = useAddServiceAccounts();
  const setServiceAccount = (sa: ServiceAccount) => {
    addServiceAccounts([sa]);
    writeBackend(sa);
  };
  const setServiceAccountName = (name: string) => {
    if (!serviceAccount) return;
    // Guard empty — the OQL validate-name-hook rejects an empty spec.name.
    if (name.trim() === "") return;
    const newSa: ServiceAccount = {
      ...serviceAccount,
      "service-account-spec": {
        ...serviceAccount["service-account-spec"],
        name,
      },
    };
    setServiceAccount(newSa);
  };
  const setDefaultDevcontainer = (devcontainerId: string | null) => {
    if (!serviceAccount) return;
    const spec = { ...serviceAccount["service-account-spec"] };
    if (devcontainerId) spec["default-devcontainer-id"] = devcontainerId;
    else delete spec["default-devcontainer-id"];
    setServiceAccount({ ...serviceAccount, "service-account-spec": spec });
  };
  return { serviceAccount, setServiceAccount, setServiceAccountName, setDefaultDevcontainer };
};

// The block carries app-id/page-id/block-id — the three ids the polymorphic
// getter resolves to the linked SA object.
type ServiceAccountBlockIds = {
  "app-id": string;
  "page-id": string;
  "block-id": string;
};

export const useFetchServiceAccount = () => {
  const addServiceAccounts = useAddServiceAccounts();

  return (block: ServiceAccountBlockIds) => {
    return getServiceAccount({
      "app-id": block["app-id"],
      "page-id": block["page-id"],
      "block-id": block["block-id"],
    }).then((sa) => {
      // Keyed by the returned object's own service-account-id.
      if (sa) addServiceAccounts([sa]);
      return sa;
    });
  };
};

export const useServiceAccountLoad = (
  block: (ServiceAccountBlockIds & { "service-account-id": string }) | undefined,
) => {
  const fetchServiceAccount = useFetchServiceAccount();
  const trackLoad = useTrackedLoad();
  const { serviceAccount } = useServiceAccount(block?.["service-account-id"]);
  const appId = block?.["app-id"];
  const pageId = block?.["page-id"];
  const blockId = block?.["block-id"];
  const key = blockId ? loadKey("service-account", blockId) : undefined;
  const error = useLoadError(key);
  const retry = () =>
    appId && pageId && blockId && key
      ? trackLoad(key, requireRecord(fetchServiceAccount({ "app-id": appId, "page-id": pageId, "block-id": blockId }), "Service account"))
      : Promise.resolve(undefined);
  useEffect(() => {
    if (!appId || !pageId || !blockId) return;
    retry();
  }, [appId, pageId, blockId]);
  return { serviceAccount, status: loadStatus(serviceAccount, error), retry };
};

export const useFetchServiceAccountListener = (
  block: ServiceAccountBlockIds | undefined,
) => {
  const fetchServiceAccount = useFetchServiceAccount();
  const appId = block?.["app-id"];
  const pageId = block?.["page-id"];
  const blockId = block?.["block-id"];
  useEffect(() => {
    if (!appId || !pageId || !blockId) return;
    fetchServiceAccount({
      "app-id": appId,
      "page-id": pageId,
      "block-id": blockId,
    });
  }, [appId, pageId, blockId]);
};

export const useServiceAccounts = () => {
  const saMap = useSelector(selectServiceAccounts);
  const serviceAccounts = Object.values(saMap);
  return { serviceAccounts };
};
